import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Drawer, Form, Select, Space } from 'antd';
import debounce from 'lodash.debounce';
import type { ITransportFilter } from 'shared/stores/use-transport-filters';
import { useTransportFiltersStore } from 'shared/stores/use-transport-filters';
import { transformAndSetFilters } from 'shared/utils/filters/transform-and-set-filters.ts';
import { CarrierMultiSelectFilter } from 'components/common/carrier-multi-select-filter/carrier-multi-select-filter.tsx';
import { TRANSPORT_SUBTYPE_OPTIONS, TRANSPORT_TYPE_OPTIONS, TRIP_ASSIGNMENT_OPTIONS } from './constants';
import styles from './transport-filters-drawer.module.scss';
import type { ITransportFiltersProps } from './types';

export const TransportFiltersDrawer: React.FC<ITransportFiltersProps> = (props) => {
  const { form, isOpen, onClose } = props;
  const { t } = useTranslation();
  const { filters, search, setFilters } = useTransportFiltersStore();

  const handleSuccess = debounce(() => transformAndSetFilters<ITransportFilter>(form, setFilters), 300);

  const handleClearFilters = () => {
    setFilters(undefined);
    setTimeout(() => form.resetFields(), 0);
    onClose();
  };

  const clearButton = filters ? (
    <Button type='link' size='small' onClick={handleClearFilters}>
      {t('common:clear_filters')}
    </Button>
  ) : null;

  const tripAssignmentOptions = TRIP_ASSIGNMENT_OPTIONS.map(({ value, labelKey }) => ({
    value,
    label: t(labelKey),
  }));

  return (
    <Drawer placement='right' open={isOpen} extra={[clearButton]} onClose={onClose}>
      <Form form={form} name='transport-filter' layout='horizontal' initialValues={filters}>
        <Space direction='vertical' size='middle' className={styles.box}>
          <Form.Item name='type' className={styles.input}>
            <Select
              placeholder={t('common:type')}
              disabled={!!search}
              options={TRANSPORT_TYPE_OPTIONS}
              allowClear
              onChange={handleSuccess}
            />
          </Form.Item>

          <Form.Item name='subTypes' className={styles.input}>
            <Select
              optionFilterProp='label'
              placeholder={t('transport:sub_type')}
              disabled={!!search}
              options={TRANSPORT_SUBTYPE_OPTIONS}
              mode='multiple'
              showSearch
              allowClear
              onChange={handleSuccess}
            />
          </Form.Item>

          <CarrierMultiSelectFilter
            name='subcontractor'
            form={form}
            formInputClassName={styles.input}
            onSetValue={handleSuccess}
          />

          <Form.Item name='tripAssignment' className={styles.input}>
            <Select
              placeholder={t('transport:trip_assignment')}
              disabled={!!search}
              options={tripAssignmentOptions}
              allowClear
              onChange={handleSuccess}
            />
          </Form.Item>

          <Form.Item name='needService' className={styles.input} valuePropName='checked'>
            <Checkbox onChange={handleSuccess}>{t('transport:needs_service')}</Checkbox>
          </Form.Item>

          <Form.Item name='inService' className={styles.input} valuePropName='checked'>
            <Checkbox onChange={handleSuccess}>{t('transport:in_service')}</Checkbox>
          </Form.Item>

          <Form.Item name='notUsed' className={styles.input} valuePropName='checked'>
            <Checkbox onChange={handleSuccess}>{t('transport:not_used')}</Checkbox>
          </Form.Item>
        </Space>
      </Form>
    </Drawer>
  );
};
